// 1:1 port of prototype/components/hud/SeasonClock.jsx (design D3): same name,
// same prop contract (prototype/components/hud/SeasonClock.d.ts), styled only
// from token CSS vars. UI.md §4/§5: the diegetic battle-royale timer, always
// visible; display-only (no interactive elements, so no hit-target concern).

import type { CSSProperties } from 'react';

export interface SeasonClockProps {
  /** Match progress 0-1 across the summer/autumn/winter split (40/35/25). */
  progress?: number;
  width?: number;
  style?: CSSProperties;
}

/** The season bands, in `Season` enum order, as cumulative upper bounds on match progress
 * (DESIGN §14's 40/35/25 split). Exported because the rounds review screen derives its
 * ground season from a trace's progress the same way — a trace manifest carries no season
 * — and two copies of these boundaries could disagree about which season it is while the
 * clock and the ground sit on the same screen. */
export const SEASON_BANDS = [
  { name: 'Summer', to: 0.4, color: 'var(--season-summer)' },
  { name: 'Autumn', to: 0.75, color: 'var(--season-autumn)' },
  { name: 'Winter', to: 1.0, color: 'var(--season-winter)' },
] as const;

const SEASONS = SEASON_BANDS;

export function SeasonClock({ progress = 0, width = 180, style }: SeasonClockProps) {
  const cur = SEASONS.find((s) => progress <= s.to) ?? SEASONS[2];
  return (
    <div
      style={{
        display: 'inline-flex',
        flexDirection: 'column',
        gap: 4,
        padding: '8px 12px',
        background: 'var(--hud-bg)',
        border: '1.5px solid var(--hud-line)',
        borderRadius: 'var(--r-md)',
        boxShadow: 'var(--shadow-hud)',
        width,
        boxSizing: 'border-box',
        ...style,
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <span
          style={{
            fontFamily: 'var(--font-body)',
            fontWeight: 800,
            fontSize: 10,
            textTransform: 'uppercase',
            letterSpacing: 'var(--tracking-caps)',
            color: cur.color,
          }}
        >
          {cur.name}
        </span>
        <span
          style={{
            fontFamily: 'var(--font-display)',
            fontWeight: 600,
            fontSize: 12,
            color: 'var(--hud-text-dim)',
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {Math.round((1 - progress) * 30)} min left
        </span>
      </div>
      <div
        style={{
          position: 'relative',
          height: 6,
          borderRadius: 3,
          overflow: 'hidden',
          background: 'var(--hud-track)',
        }}
      >
        {SEASONS.map((s, i) => {
          const from = i === 0 ? 0 : SEASONS[i - 1].to;
          return (
            <div
              key={s.name}
              style={{
                position: 'absolute',
                left: `${from * 100}%`,
                width: `${(s.to - from) * 100}%`,
                top: 0,
                bottom: 0,
                background: s.color,
                opacity: 0.55,
              }}
            />
          );
        })}
        <div
          style={{
            position: 'absolute',
            left: `${progress * 100}%`,
            top: -1,
            width: 2.5,
            height: 8,
            background: '#2B2114',
            borderRadius: 1,
          }}
        />
      </div>
    </div>
  );
}
